/**
 * The last account confirmed on this device, kept so the app can open as them with no signal.
 *
 * A **hint, not a credential**. It is the account's public profile (id, name, contact) and
 * nothing that authenticates anyone; the session is the httpOnly cookie, which this code cannot
 * read (ADR-0008). Reading it out of IndexedDB gives an attacker a user id and no way to use it.
 *
 * Kept in IndexedDB rather than `localStorage` so the service worker can read it too — the
 * background drain needs to know whose balls it may send — and in the existing key-value store
 * rather than a new one, because adding an object store means bumping the database version,
 * and a version bump blocks until every open tab from the old version closes.
 */
import type { AuthUser } from "@sportsos/engine";
import { CURRENT, asPromise, committed, database } from "./db.ts";

const KEY = "identity";

export async function saveIdentityHint(user: AuthUser | null): Promise<void> {
  const db = await database();
  const tx = db.transaction(CURRENT, "readwrite");
  if (user === null) tx.objectStore(CURRENT).delete(KEY);
  else tx.objectStore(CURRENT).put(user, KEY);
  await committed(tx);
}

export async function loadIdentityHint(): Promise<AuthUser | null> {
  const db = await database();
  const tx = db.transaction(CURRENT, "readonly");
  const found = await asPromise<AuthUser | undefined>(tx.objectStore(CURRENT).get(KEY));
  await committed(tx);
  return found ?? null;
}
